import { AppsFlyer } from '../plugins/apps_flyer_plugin.js';
import { analytics, AnalyticsPropertyKey } from '../analytics/index.js';
import { OfferEventType } from '../api/dto/offers.dto.js';
import type { OfferDto } from '../api/dto/offers.dto.js';
import { GlobalSettings } from '../global_settings.js';

enum OfferAnalyticsProvider
{
  AppsFlyer = 'appsflyer',
  Athena    = 'athena',
}

enum OfferStep
{
  PlacementImpression = 'placement_impression',
  Shown            = 'shown',
  Clicked          = 'clicked',
  Close            = 'close',
  PriceLoaded      = 'price_loaded',
  PriceUnavailable = 'price_unavailable',
  Purchase         = 'purchase',
  FreeClaim        = 'free_claim',
}

enum OfferPurchaseStatus
{
  Started = 'started',
  Success = 'success',
  Failed  = 'failed',
}

const OFFER_STEP_BY_EVENT: Record<OfferEventType, OfferStep> = {
  [OfferEventType.PlacementImpression]: OfferStep.PlacementImpression,
  [OfferEventType.Shown]:            OfferStep.Shown,
  [OfferEventType.Clicked]:          OfferStep.Clicked,
  [OfferEventType.Close]:            OfferStep.Close,
  [OfferEventType.PriceLoaded]:      OfferStep.PriceLoaded,
  [OfferEventType.PriceUnavailable]: OfferStep.PriceUnavailable,
  [OfferEventType.PurchaseStarted]:  OfferStep.Purchase,
  [OfferEventType.PurchaseSuccess]:  OfferStep.Purchase,
  [OfferEventType.PurchaseFailed]:   OfferStep.Purchase,
  [OfferEventType.FreeClaim]:        OfferStep.FreeClaim,
};

const PURCHASE_STATUS_BY_EVENT: Partial<Record<OfferEventType, OfferPurchaseStatus>> = {
  [OfferEventType.PurchaseStarted]: OfferPurchaseStatus.Started,
  [OfferEventType.PurchaseSuccess]: OfferPurchaseStatus.Success,
  [OfferEventType.PurchaseFailed]:  OfferPurchaseStatus.Failed,
};

const APPS_FLYER_PURCHASE_EVENT = 'af_purchase';
const OFFER_SHOWN_EVENT = 'offer_shown';
const OFFER_ACCEPTED_EVENT = 'offer_accepted';
const DEFAULT_APPS_FLYER_EVENT_BY_TYPE: Record<OfferEventType, string> = {
  [OfferEventType.PlacementImpression]: 'offer_placement_impression',
  [OfferEventType.Shown]: 'offer_shown',
  [OfferEventType.Clicked]: 'offer_clicked',
  [OfferEventType.Close]: 'offer_closed',
  [OfferEventType.PriceLoaded]: 'offer_price_loaded',
  [OfferEventType.PriceUnavailable]: 'offer_price_unavailable',
  [OfferEventType.PurchaseStarted]: 'offer_purchase_started',
  [OfferEventType.PurchaseSuccess]: 'offer_purchase_success',
  [OfferEventType.PurchaseFailed]: 'offer_purchase_failed',
  [OfferEventType.FreeClaim]: 'offer_free_claim',
};

export interface OfferPriceAnalyticsContext
{
  productid?: string;
  formatted_price?: string;
  currency?: string;
  price_micros?: number | string;
}

export interface AppsFlyerPurchaseContext extends OfferPriceAnalyticsContext
{
  chain_step_id?: string;
  transaction_id?: string;
  store?: string;
  purchase_type?: string;
  is_test?: boolean;
  placement?: string;
  placement_id?: string;
  template?: string;
  priceusd?: number | null;
  originalusd?: number | null;
  revenue_usd?: number | null;
  offer_step?: number;
}

function isObject( value: unknown ): value is Record<string, unknown>
{
  return !!value && typeof value === 'object' && !Array.isArray( value );
}

function readEventName( offer: OfferDto, event_type: OfferEventType, provider: OfferAnalyticsProvider ): string
{
  const data = offer.data ?? {};
  const analytics_cfg = isObject( data.analytics ) ? data.analytics : {};
  const events = isObject( analytics_cfg.events ) ? analytics_cfg.events : {};
  const override = events[event_type];

  if ( typeof override === 'string' && override.trim() ) return override.trim();
  if ( isObject( override ) )
  {
    const value = provider === OfferAnalyticsProvider.AppsFlyer
      ? override.appsflyer ?? override.appsFlyer
      : override.athena ?? override.afina;
    if ( typeof value === 'string' && value.trim() ) return value.trim();
  }

  const fallback = provider === OfferAnalyticsProvider.AppsFlyer
    ? analytics_cfg.appsflyer ?? analytics_cfg.appsFlyer
    : analytics_cfg.athena ?? analytics_cfg.afina;
  if ( typeof fallback === 'string' && fallback.trim() ) return fallback.trim();

  return DEFAULT_APPS_FLYER_EVENT_BY_TYPE[event_type];
}

function buildProperties(
  offer: OfferDto,
  event_type: OfferEventType,
  extra?: Record<string, unknown>,
): Record<string, unknown>
{
  const purchase_status = PURCHASE_STATUS_BY_EVENT[event_type];
  const placement_id = typeof extra?.placement_id === 'string' ? extra.placement_id : offer.placement?.id;
  const limit = offer.purchase_limit;

  return {
    [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
    sourceId: `offer_${offer.id}`,
    offerid: offer.id,
    offer_id: offer.id,
    entitlementkey: offer.entitlement_key,
    offertype: offer.presentation ?? offer.kind ?? 'unknown',
    offer_kind: offer.presentation ?? offer.kind ?? 'unknown',
    purchase_type: offer.purchase?.type,
    offer_event: event_type,
    offerstep: OFFER_STEP_BY_EVENT[event_type],
    // Configuration the offer was shown with: lets conversion be split by
    // visual / entry point / limit without joining the catalog afterwards.
    ...( offer.shop_section ? { shop_section: offer.shop_section } : {} ),
    ...( offer.visual ? { visual: offer.visual } : {} ),
    ...( offer.badge ? { badge: offer.badge } : {} ),
    ...( offer.price_usd !== undefined && !offer.chain ? { originalusd: offer.price_usd, priceusd: offer.price_usd } : {} ),
    ...( offer.placement?.template ? { template: offer.placement.template } : {} ),
    ...( offer.trigger ? { trigger_events: offer.trigger.events.join( ',' ) } : {} ),
    ...( limit ? {
      purchase_limit_max: limit.max,
      purchase_limit_reset: limit.reset.type,
      ...( offer.purchases_left !== undefined ? { purchases_left: offer.purchases_left } : {} ),
      ...( offer.limit_resets_at ? { limit_resets_at: offer.limit_resets_at } : {} ),
    } : {} ),
    ...( placement_id ? { [AnalyticsPropertyKey.PLACEMENTID]: placement_id, placement_id } : {} ),
    ...( purchase_status ? { status: purchase_status } : {} ),
    af_content_id: offer.id,
    af_content_type: 'offer',
    ...extra,
  };
}

function buildAthenaProperties(
  offer: OfferDto,
  event_type: OfferEventType,
  extra?: Record<string, unknown>,
): Record<string, unknown>
{
  return buildProperties( offer, event_type, extra );
}

export function buildOfferPriceAnalyticsProperties(
  price?: OfferPriceAnalyticsContext | null,
): Record<string, unknown>
{
  if ( !price ) return {};

  const price_micros = price.price_micros;
  const micros_as_number = typeof price_micros === 'string' ? Number( price_micros ) : price_micros;
  const revenue = typeof micros_as_number === 'number' && Number.isFinite( micros_as_number )
    ? micros_as_number / 1_000_000
    : undefined;

  return {
    ...( price.productid ? { productid: price.productid } : {} ),
    ...( price.formatted_price ? {
      formatted_price: price.formatted_price,
      formattedprice: price.formatted_price,
    } : {} ),
    ...( price.currency ? {
      currency: price.currency,
    } : {} ),
    ...( price_micros != null ? { price_micros } : {} ),
    ...( revenue != null ? {
      price: revenue,
      revenue,
    } : {} ),
  };
}

export async function trackAppsFlyerPurchase(
  offer: OfferDto,
  purchase: AppsFlyerPurchaseContext,
): Promise<void>
{
  if ( !GlobalSettings.IS_PROD )
  {
    console.info( '[AppsFlyer] af_purchase skipped: non-production environment', {
      offer_id: offer.id,
      product_id: purchase.productid,
    } );
    return;
  }

  if ( purchase.is_test === true )
  {
    console.info( '[AppsFlyer] af_purchase skipped: test purchase', {
      offer_id: offer.id,
      product_id: purchase.productid,
    } );
    return;
  }

  const price_properties = buildOfferPriceAnalyticsProperties( purchase );
  const revenue = price_properties.revenue;
  const currency = typeof purchase.currency === 'string' ? purchase.currency.trim().toUpperCase() : '';

  // AppsFlyer must receive both values to attribute revenue in the correct currency.
  // If store pricing is unavailable, keep the custom purchase_success event but do not
  // report an incomplete revenue event (AppsFlyer would otherwise assume USD).
  if ( typeof revenue !== 'number' || !Number.isFinite( revenue ) || !currency )
  {
    console.warn( '[AppsFlyer] af_purchase skipped: price or currency is missing', {
      offer_id: offer.id,
      product_id: purchase.productid,
    } );
    return;
  }

  await AppsFlyer.logEvent( APPS_FLYER_PURCHASE_EVENT, {
    af_revenue: revenue,
    af_currency: currency,
    af_order_id: purchase.transaction_id,
    product_id: purchase.productid ?? offer.id,
    af_content_id: purchase.productid ?? offer.id,
    af_content_type: 'offer',
    af_quantity: 1,
    offer_id: offer.id,
    offerid: offer.id,
    offertype: offer.presentation ?? offer.kind ?? 'unknown',
    offer_kind: offer.presentation ?? offer.kind ?? 'unknown',
    offerstep: OfferStep.Purchase,
    status: OfferPurchaseStatus.Success,
    entitlementkey: offer.entitlement_key,
    ...( purchase.chain_step_id ? { chain_step_id: purchase.chain_step_id } : {} ),
    ...( purchase.store ? { store: purchase.store } : {} ),
    ...( purchase.purchase_type ? { purchase_type: purchase.purchase_type } : {} ),
    ...( purchase.placement ? { placement: purchase.placement } : {} ),
    ...( purchase.placement_id ? { placement_id: purchase.placement_id } : {} ),
    ...( purchase.placement_id ? { placementid: purchase.placement_id } : {} ),
    ...( purchase.template ? { template: purchase.template } : {} ),
    ...( offer.visual ? { visual: offer.visual } : {} ),
    // USD contract shared with the analytics team; af_revenue above stays in store currency.
    ...( purchase.priceusd !== undefined ? { priceusd: purchase.priceusd } : {} ),
    ...( purchase.originalusd !== undefined ? { originalusd: purchase.originalusd } : {} ),
    ...( purchase.revenue_usd !== undefined ? { revenue_usd: purchase.revenue_usd } : {} ),
    ...( purchase.offer_step !== undefined ? { offer_step: purchase.offer_step } : {} ),
  } );
}

export async function trackOfferAnalytics(
  offer: OfferDto,
  event_type: OfferEventType,
  extra?: Record<string, unknown>,
): Promise<void>
{
  const props = buildProperties( offer, event_type, extra );
  analytics.track( 'offer', buildAthenaProperties( offer, event_type, extra ) );

  // Server validation is the authoritative source for test purchases. Athena
  // keeps the event for QA visibility, while AppsFlyer must stay production-only.
  if ( PURCHASE_STATUS_BY_EVENT[event_type]
    && ( !GlobalSettings.IS_PROD || extra?.is_test === true ) ) return;

  const af_name = readEventName( offer, event_type, OfferAnalyticsProvider.AppsFlyer );

  if ( event_type === OfferEventType.Shown )
  {
    await AppsFlyer.logEvent( OFFER_SHOWN_EVENT, {
      ...props,
    } );
  }

  if ( event_type === OfferEventType.PurchaseSuccess && extra?.entitlement_active === true )
  {
    const revenue = extra.revenue;
    const currency = typeof extra.currency === 'string' ? extra.currency.trim().toUpperCase() : '';
    if ( typeof revenue === 'number' && Number.isFinite( revenue ) && currency )
    {
      await AppsFlyer.logEvent( OFFER_ACCEPTED_EVENT, {
        ...props,
        af_revenue: revenue,
        af_currency: currency,
      } );
    }
    else
    {
      console.warn( '[AppsFlyer] offer_accepted skipped: price or currency is missing', {
        offer_id: offer.id,
      } );
    }
  }

  // af_purchase is emitted separately after server validation. Avoid a duplicate when
  // an older remote offer config maps purchase_success to the predefined event name.
  if ( event_type === OfferEventType.PurchaseSuccess && af_name === APPS_FLYER_PURCHASE_EVENT ) return;
  if ( event_type === OfferEventType.Shown && af_name === OFFER_SHOWN_EVENT ) return;
  if ( event_type === OfferEventType.PurchaseSuccess && af_name === OFFER_ACCEPTED_EVENT ) return;

  await AppsFlyer.logEvent( af_name, props );
}
